class TextTruncator {
  constructor(maxChars = 59) {
    this.maxChars = maxChars;
  }

  truncate(text) {
    if (text.length <= this.maxChars) return text;
    return text.slice(0, this.maxChars).trimEnd() + '...';
  }

  truncateByWords(text, maxWords = 25) {
    const words = text.split(' ');
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + '...';
  }

  truncateHtmlText(htmlText) {
    const plainText = htmlText.replace(/<[^>]*>/g, '');
    return this.truncate(plainText);
  }

  setMaxChars(maxChars) {
    this.maxChars = maxChars;
  }

  shouldTruncate(text) {
    return text.length > this.maxChars;
  }
}

class ExpandableText {
  constructor(element, truncator) {
    this.element = element;
    this.truncator = truncator;
    this.originalText = element.textContent.trim();
    
    if (!this.element.dataset.originalText) {
      this.element.dataset.originalText = this.originalText;
    }
    
    this.init();
  }

  init() {
    if (!this.truncator.shouldTruncate(this.originalText)) return;

    this.truncatedText = this.truncator.truncate(this.originalText);
    this.element.dataset.truncated = 'true';
    this.element.dataset.expanded = 'false';
    this.#render(false);
    this.element.style.cursor = 'pointer';
  }

  #render(isExpanded) {
    const text = isExpanded ? this.originalText : this.truncatedText;
    const linkText = isExpanded ? 'see less' : 'see more';
    const link = `<span class="expand-link font-mulish-bold">${linkText}</span>`;
    this.element.innerHTML = text + ' ' + link;
    this.element.dataset.expanded = isExpanded ? 'true' : 'false';
  }

  static toggle(element, truncator) {
    const isExpanded = element.dataset.expanded === 'true';
    const originalText = element.dataset.originalText;
    const truncatedText = truncator.truncate(originalText);
    const newState = !isExpanded;
    
    const text = newState ? originalText : truncatedText;
    const linkText = newState ? 'see less' : 'see more';
    const link = `<span class="expand-link font-mulish-bold">${linkText}</span>`;
    
    element.innerHTML = text + ' ' + link;
    element.dataset.expanded = newState ? 'true' : 'false';
  }
}

if (!customElements.get('testimonials-component')) {
  class Testimonials extends HTMLElement {
    #truncator;
    #sliderInstance = false;
    #delegatedClickHandler = null;
    #selectors = {
      sliderWrapper: '.testimonials__items',
      pagination: '.swiper-pagination',
      nextEl: '.swiper-btn-next',
      prevEl: '.swiper-btn-prev',
      testimonialContent: '.testimonial__content',
    };
    #classes = {
      grid: 'f-grid',
      swiper: 'swiper',
      swiperWrapper: 'swiper-wrapper',
    };

    constructor() {
      super();
      this.#truncator = new TextTruncator();
    }

    connectedCallback() {
      this.#setupProperties();
      
      this.#applyTextTruncation();
      
      this.#setupGlobalClickHandler();

      if (!this.#isSliderEnabled()) return;

      this.#initializeEventListeners();
      this.#initialize();
    }

    disconnectedCallback() {
      if (this.#delegatedClickHandler) {
        document.removeEventListener('click', this.#delegatedClickHandler);
      }
    }

    #setupProperties() {
      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.#selectors.sliderWrapper);
      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items);
      this.tabletItems = parseInt(this.dataset.tabletItems);

      this.#truncator.setMaxChars(59);
    }

    #isSliderEnabled() {
      return this.enableSlider;
    }

    #setupGlobalClickHandler() {
      this.#delegatedClickHandler = (e) => {
        const testimonialContent = e.target.closest(this.#selectors.testimonialContent);
        
        if (!testimonialContent) return;
        if (!this.contains(testimonialContent)) return;
        
        if (testimonialContent.dataset.truncated !== 'true') return;
        
        e.preventDefault();
        e.stopPropagation();
        
        ExpandableText.toggle(testimonialContent, this.#truncator);
      };
      
      document.addEventListener('click', this.#delegatedClickHandler);
    }

    #initializeEventListeners() {
      document.addEventListener('matchMobile', () => this.#initialize());
      document.addEventListener('unmatchMobile', () => this.#initialize());
    }

    #initialize() {
      if (FoxTheme.config.mqlMobile) {
        this.#destroySlider();
      } else {
        this.#initSlider();
      }
      
      setTimeout(() => this.#reapplyTruncation(), 100);
    }

    #initSlider() {
      const columnGap = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--column-gap');
      const spaceBetween = parseFloat(columnGap.replace('rem', '')) * 10;

      const sliderOptions = this.#buildSliderOptions(spaceBetween);

      if (typeof this.#sliderInstance !== 'object') {
        this.#setupSliderDOM();
        this.#sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.#sliderInstance.init();

        this.#setupFocusableElements();
        
        if (this.#sliderInstance.slider) {
          this.#sliderInstance.slider.on('init', () => {
            this.#reapplyTruncation();
          });
          
          this.#sliderInstance.slider.on('slideChange', () => {
            this.#reapplyTruncation();
          });
        }
      }

      if (Shopify.designMode) {
        this.#setupDesignModeListener();
      }
    }

    #buildSliderOptions(spaceBetween) {
      return {
        slidesPerView: 2,
        spaceBetween: spaceBetween,
        navigation: {
          nextEl: this.section.querySelector(this.#selectors.nextEl),
          prevEl: this.section.querySelector(this.#selectors.prevEl),
        },
        pagination: {
          el: this.section.querySelector(this.#selectors.pagination),
          clickable: true,
          type: 'fraction',
        },
        breakpoints: {
          768: {
            slidesPerView: this.tabletItems > 3 ? 3 : parseInt(this.tabletItems),
          },
          1280: {
            slidesPerView: parseInt(this.items),
          },
        },
        loop: true,
        threshold: 2,
      };
    }

    #setupSliderDOM() {
      this.classList.add(this.#classes.swiper);
      this.sliderWrapper.classList.remove(this.#classes.grid);
      this.sliderWrapper.classList.add(this.#classes.swiperWrapper);
    }

    #applyTextTruncation() {
      const contentElements = this.querySelectorAll(this.#selectors.testimonialContent);
      
      contentElements.forEach((element) => {
        if (!element.dataset.originalText) {
          new ExpandableText(element, this.#truncator);
        }
      });
    }

    #reapplyTruncation() {
      const contentElements = this.querySelectorAll(this.#selectors.testimonialContent);
      
      contentElements.forEach((element) => {
        if (!element.dataset.truncated && element.textContent.trim().length > 0) {
          new ExpandableText(element, this.#truncator);
        }
      });
    }

    #setupFocusableElements() {
      const focusableElements = FoxTheme.a11y.getFocusableElements(this);
      focusableElements.forEach((element) => {
        element.addEventListener('focusin', () => {
          const slide = element.closest('.swiper-slide');
          this.#sliderInstance.slider.slideTo(
            this.#sliderInstance.slider.slides.indexOf(slide)
          );
        });
      });
    }

    #setupDesignModeListener() {
      if (typeof this.#sliderInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId !== this.sectionId) return;
          const index = Number(e.detail.target.dataset.index);
          this.#sliderInstance.slider.slideToLoop(index);
        });
      }
    }

    #destroySlider() {
      this.classList.remove(this.#classes.swiper);
      this.sliderWrapper.classList.remove(this.#classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.#classes.grid);

      if (typeof this.#sliderInstance === 'object') {
        this.#sliderInstance.slider.destroy();
        this.#sliderInstance = false;
      }
    }
  }

  customElements.define('testimonials-component', Testimonials);
}